import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useIsSupport } from '@treams/treams-hooks';
import { WarningIcon } from '@treams/treams-icons';
import {
	ConnectorErrorType,
	ConnectorLog,
	ConnectorLogLevel,
	useConnectorLogsQuery,
	useMarketplaceQuery,
} from '@treams/treams-network';
import { Button, Callout, Separator, Table, useToasts } from '@treams/treams-ui';

import ConnectorReplaceUserModal from './ConnectorReplaceUserModal';

export function ConnectorErrorSummary() {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const isSupport = useIsSupport();

	const today = useMemo(() => moment().format('YYYY-MM-DD'), []);
	const [incomingUser, setIncomingUser] = useState<{
		field: 'email' | 'username';
		value: string;
		externalReferenceID: string;
	} | null>(null);

	const { data, loading } = useConnectorLogsQuery({
		variables: {
			date: today,
			minimumLogLevel: ConnectorLogLevel.Error,
			first: 1000,
		},
		fetchPolicy: 'network-only',
	});

	const { data: marketplaceData } = useMarketplaceQuery();

	const workspaceConnector = marketplaceData?.viewer.workspace.connector;

	const replaceUser = useCallback((log: ConnectorLog) => {
		if (log.errorType === ConnectorErrorType.UserUsernameTakenError) {
			setIncomingUser({
				field: 'username',
				value: log.metadata!.user!.username!,
				externalReferenceID: log.metadata!.user!.externalReferenceID!,
			});
		} else {
			setIncomingUser({
				field: 'email',
				value: log.metadata!.user!.email!,
				externalReferenceID: log.metadata!.user!.externalReferenceID!,
			});
		}
	}, []);

	const errorLogs = useMemo(() => {
		if (!data) return [];

		const errorLogs = data.connectorLogs.edges.reduce<ConnectorLog[]>((logs, { node: log }) => {
			const isErrorLog = log.logLevel === ConnectorLogLevel.Error && log.metadata && log.errorType;

			if (isErrorLog) {
				logs.push(log);
			}
			return logs;
		}, []);

		const sortedErrorLogs = errorLogs.sort(
			(a: ConnectorLog, b: ConnectorLog) =>
				new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
		);

		return sortedErrorLogs;
	}, [data]);

	if (!isSupport) {
		return null;
	}

	if (loading) {
		return null;
	}

	if (!workspaceConnector || errorLogs.length === 0) {
		return null;
	}

	return (
		<Box>
			<Callout
				color="red"
				icon={<WarningIcon />}
				title={t('settings.connectors.errors.title')}
				description={
					errorLogs.length === 1
						? t('settings.connectors.errors.singleDescription', {
								connector: workspaceConnector.type,
							})
						: t('settings.connectors.errors.description', {
								count: errorLogs.length,
								connector: workspaceConnector.type,
							})
				}
				mb={3}
			/>

			<Table>
				<Table.Header>
					<Table.Cell basis={3}>{t('settings.connectors.errors.table.message')}</Table.Cell>
					<Table.Cell basis={2}>
						{t('settings.connectors.errors.table.newEmployeeEmail')}
					</Table.Cell>
					<Table.Cell basis={2}>{t('settings.connectors.errors.table.username')}</Table.Cell>
					<Table.Cell basis={2}>{t('settings.connectors.errors.table.action')}</Table.Cell>
				</Table.Header>
				{errorLogs.map((log) => (
					<Table.Row key={log.id}>
						<Table.Cell basis={3}>
							{log.errorType === ConnectorErrorType.UserUsernameTakenError && (
								<>{t('settings.connectors.errors.error.usernameTaken')}</>
							)}

							{log.errorType === ConnectorErrorType.UserEmailTakenError && (
								<>{t('settings.connectors.errors.error.emailTaken')}</>
							)}
						</Table.Cell>
						<Table.Cell basis={2}>
							{log.metadata && log.metadata.user && log.metadata.user.email}
						</Table.Cell>
						<Table.Cell basis={2}>
							{log.metadata && log.metadata.user && log.metadata.user.username}
						</Table.Cell>
						<Table.Cell basis={2}>
							{(log.errorType === ConnectorErrorType.UserUsernameTakenError ||
								log.errorType === ConnectorErrorType.UserEmailTakenError) &&
								log.metadata?.user && (
									<Button
										small
										type="transparent"
										title={t('settings.connectors.errors.replace')}
										onClick={() => replaceUser(log)}
									/>
								)}
						</Table.Cell>
					</Table.Row>
				))}
			</Table>

			<Separator my={4} />

			{incomingUser && (
				<ConnectorReplaceUserModal
					incomingUser={incomingUser}
					onDismiss={() => setIncomingUser(null)}
					onSuccess={() => {
						addToast(t('settings.connectors.errors.success'), {
							appearance: 'success',
							autoDismiss: true,
						});
						setIncomingUser(null);
					}}
				/>
			)}
		</Box>
	);
}
